import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getPostBySlug, listPublishedPosts } from "@/lib/blog.functions";

const postQuery = (slug: string) =>
  queryOptions({
    queryKey: ["blog", "post", slug],
    queryFn: () => getPostBySlug({ data: { slug } }),
  });

const relatedQuery = queryOptions({
  queryKey: ["blog", "published"],
  queryFn: () => listPublishedPosts(),
});

export const Route = createFileRoute("/ratgeber/$slug")({
  loader: async ({ context, params }) => {
    const res = await context.queryClient.ensureQueryData(postQuery(params.slug));
    if (!res.post) throw notFound();
    void context.queryClient.ensureQueryData(relatedQuery);
    return res;
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    if (!post) return { meta: [{ title: "Artikel nicht gefunden — PIXIWEB" }, { name: "robots", content: "noindex, follow" }] };
    const url = `https://pixiweb.de/ratgeber/${params.slug}`;
    const desc = (post.meta_description || post.excerpt || "").slice(0, 160);
    const title = `${post.title} | PIXIWEB Ratgeber`;
    const modified = post.updated_at ?? post.published_at ?? undefined;
    const image = post.cover_image_url ?? undefined;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { name: "author", content: post.author_name },
        { name: "keywords", content: [post.tag, "Webdesign", "PIXIWEB", "Ratgeber"].join(", ") },
        // Open Graph
        { property: "og:type", content: "article" },
        { property: "og:site_name", content: "PIXIWEB" },
        { property: "og:locale", content: "de_DE" },
        { property: "og:title", content: post.title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        ...(image
          ? [
              { property: "og:image", content: image },
              { property: "og:image:secure_url", content: image },
              { property: "og:image:alt", content: post.title },
              { property: "og:image:width", content: "1200" },
              { property: "og:image:height", content: "630" },
            ]
          : []),
        // Twitter
        { name: "twitter:card", content: image ? "summary_large_image" : "summary" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: desc },
        ...(image ? [{ name: "twitter:image", content: image }, { name: "twitter:image:alt", content: post.title }] : []),
        // Article
        ...(post.published_at ? [{ property: "article:published_time", content: post.published_at }] : []),
        ...(modified ? [{ property: "article:modified_time", content: modified }] : []),
        { property: "article:author", content: post.author_name },
        { property: "article:section", content: post.tag },
        { property: "article:tag", content: post.tag },
        { property: "article:publisher", content: "https://pixiweb.de" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "@id": `${url}#article`,
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            headline: post.title.slice(0, 110),
            name: post.title,
            description: desc,
            url,
            inLanguage: "de-DE",
            articleSection: post.tag,
            keywords: post.tag,
            wordCount: post.word_count,
            ...(image ? { image: { "@type": "ImageObject", url: image, width: 1200, height: 630 } } : {}),
            datePublished: post.published_at ?? undefined,
            dateModified: modified,
            author: {
              "@type": "Organization",
              name: post.author_name,
              url: "https://pixiweb.de",
            },
            publisher: {
              "@type": "Organization",
              name: "PIXIWEB",
              url: "https://pixiweb.de",
              logo: { "@type": "ImageObject", url: "https://pixiweb.de/favicon.ico" },
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://pixiweb.de/" },
              { "@type": "ListItem", position: 2, name: "Ratgeber", item: "https://pixiweb.de/ratgeber" },
              { "@type": "ListItem", position: 3, name: post.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
  errorComponent: ({ error }) => (
    <div className="container-pixi py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-navy">Artikel nicht ladbar</h1>
      <p className="mt-3 text-slate">{error instanceof Error ? error.message : "Unbekannter Fehler"}</p>
      <Link to="/ratgeber" className="mt-6 inline-block rounded-xl bg-navy px-5 py-2.5 text-sm font-semibold text-white">Zurück zum Ratgeber</Link>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-pixi py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-navy">Artikel nicht gefunden</h1>
      <Link to="/ratgeber" className="mt-6 inline-block rounded-xl bg-navy px-5 py-2.5 text-sm font-semibold text-white">Zurück zum Ratgeber</Link>
    </div>
  ),
});

function BlogPostPage() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(postQuery(slug));
  const { data: related } = useSuspenseQuery(relatedQuery);
  const post = data.post!;
  const others = related.rows.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <article>
      <header className="border-b border-mist bg-ice">
        <div className="container-pixi py-12 md:py-20">
          <Link to="/ratgeber" className="text-sm text-slate hover:text-electric">← Alle Ratgeber-Artikel</Link>
          <div className="mt-6 flex items-center gap-3 text-xs text-slate">
            <span className="rounded-full bg-white px-3 py-1 font-medium text-navy">{post.tag}</span>
            <span>{post.reading_minutes} Min. Lesezeit</span>
            {post.published_at && (
              <>
                <span>·</span>
                <time dateTime={post.published_at}>
                  {new Date(post.published_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" })}
                </time>
              </>
            )}
          </div>
          <h1 className="mt-4 max-w-4xl font-display text-4xl font-bold text-navy md:text-5xl">{post.title}</h1>
          {post.excerpt && <p className="mt-4 max-w-3xl text-lg text-slate">{post.excerpt}</p>}
          <p className="mt-6 text-sm text-slate">von <span className="font-medium text-navy">{post.author_name}</span></p>
        </div>
      </header>

      {post.cover_image_url && (
        <div className="container-pixi mt-8">
          <img
            src={post.cover_image_url}
            alt={post.title}
            loading="eager"
            decoding="async"
            className="aspect-[16/9] w-full rounded-2xl object-cover"
          />
        </div>
      )}

      <div className="container-pixi py-12 md:py-16">
        <div className="prose-pixi mx-auto max-w-3xl">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body_markdown}</ReactMarkdown>
        </div>

        <div className="mx-auto mt-16 max-w-3xl rounded-2xl border border-mist bg-gradient-to-br from-navy to-electric p-8 text-primary-foreground">
          <h2 className="font-display text-2xl font-bold text-primary-foreground">Brauchst du eine neue Website?</h2>
          <p className="mt-2 text-primary-foreground/90">Lass uns über dein Projekt sprechen — unverbindlich und mit einem auf dich zugeschnittenen Angebot.</p>
          <a href="/#kontakt" className="mt-6 inline-block rounded-xl bg-background px-6 py-3 text-sm font-semibold text-foreground hover:bg-secondary">
            Projekt anfragen
          </a>
        </div>
      </div>

      {others.length > 0 && (
        <section className="border-t border-mist bg-ice">
          <div className="container-pixi py-12 md:py-16">
            <h2 className="font-display text-2xl font-bold text-navy">Weitere Artikel</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {others.map((p) => (
                <Link
                  key={p.id}
                  to="/ratgeber/$slug"
                  params={{ slug: p.slug }}
                  className="rounded-xl border border-mist bg-white p-5 transition hover:shadow-pixi"
                >
                  <div className="text-xs text-slate">{p.tag} · {p.reading_minutes} Min.</div>
                  <div className="mt-2 font-display text-lg font-bold text-navy">{p.title}</div>
                  <div className="mt-2 line-clamp-2 text-sm text-slate">{p.excerpt}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
